import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, AlertTriangle, ShieldCheck, Ban, Camera, MessageSquare, CreditCard, Flag } from 'lucide-react';

const PostingRules = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isAr = i18n.language === 'ar';

  const rules = isAr ? [
    { icon: Camera, title: 'صور حقيقية فقط', desc: 'استخدم صورًا حقيقية للمنتج. يُمنع استخدام صور من الإنترنت أو صور مخزنة. يجب أن تكون الصور واضحة وتعكس حالة المنتج الفعلية.' },
    { icon: Ban, title: 'ممنوع المحتوى المخالف', desc: 'يُمنع نشر إعلانات للمنتجات غير القانونية أو المقلدة أو المسروقة أو الأسلحة أو المخدرات أو أي محتوى يخالف قوانين دولة الإمارات.' },
    { icon: CreditCard, title: 'أسعار حقيقية', desc: 'يجب أن يعكس السعر المعلن القيمة الفعلية للمنتج. يُمنع وضع أسعار وهمية أو مضللة لجذب المشترين.' },
    { icon: MessageSquare, title: 'وصف دقيق', desc: 'اكتب وصفًا دقيقًا وصادقًا للمنتج يتضمن الحالة والمواصفات وأي عيوب موجودة.' },
    { icon: Flag, title: 'فئة صحيحة', desc: 'انشر إعلانك في الفئة المناسبة. الإعلانات في الفئات الخاطئة سيتم حذفها.' },
    { icon: Ban, title: 'إعلان واحد لكل منتج', desc: 'يُمنع نشر نفس الإعلان أكثر من مرة. الإعلانات المكررة سيتم حذفها تلقائيًا.' },
    { icon: ShieldCheck, title: 'معلومات تواصل صحيحة', desc: 'تأكد من أن رقم هاتفك وطريقة التواصل صحيحة ومحدثة.' },
    { icon: AlertTriangle, title: 'الإعلانات المخالفة', desc: 'نحتفظ بالحق في حذف أي إعلان مخالف لهذه القواعد دون إشعار مسبق. التكرار قد يؤدي لحظر الحساب.' },
  ] : [
    { icon: Camera, title: 'Real Photos Only', desc: 'Use actual photos of your item. Stock images and internet-sourced photos are not allowed. Photos must be clear and reflect the actual condition.' },
    { icon: Ban, title: 'No Prohibited Content', desc: 'Ads for illegal, counterfeit, stolen goods, weapons, drugs, or any content violating UAE laws are strictly forbidden.' },
    { icon: CreditCard, title: 'Honest Pricing', desc: 'The listed price must reflect the actual value. Fake or misleading prices to attract buyers are not allowed.' },
    { icon: MessageSquare, title: 'Accurate Description', desc: 'Write an honest and detailed description including condition, specifications, and any existing defects.' },
    { icon: Flag, title: 'Correct Category', desc: 'Post your ad in the appropriate category. Ads placed in wrong categories will be removed.' },
    { icon: Ban, title: 'One Ad Per Item', desc: 'Duplicate ads for the same item are not allowed and will be automatically removed.' },
    { icon: ShieldCheck, title: 'Valid Contact Info', desc: 'Ensure your phone number and contact method are correct and up to date.' },
    { icon: AlertTriangle, title: 'Violations', desc: 'We reserve the right to remove any ad violating these rules without prior notice. Repeated violations may result in account suspension.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460] pb-32" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="hero-gradient relative">
        <div className="noise-overlay" />
        <div className="max-w-2xl mx-auto px-4 pt-4 pb-6 relative z-10">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-white/8 flex items-center justify-center hover:bg-white/15 transition-all active:scale-90 mb-4">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-2xl font-extrabold text-white font-heading">{isAr ? 'قواعد النشر' : 'Posting Rules'}</h1>
          <p className="text-white/40 text-sm mt-1">{isAr ? 'يرجى الالتزام بهذه القواعد عند نشر إعلانك' : 'Please follow these rules when posting your ad'}</p>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-3">
        {rules.map((rule, i) => {
          const Icon = rule.icon;
          return (
            <div key={i} className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-4 flex gap-3">
              <div className="w-10 h-10 bg-[#E31C25]/15 rounded-xl flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#E31C25]" />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm mb-1">{rule.title}</h3>
                <p className="text-white/50 text-xs leading-relaxed">{rule.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PostingRules;
